import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { sections } from "./sections";
import { Frame } from "./components/Frame";
import { PageIndex } from "./components/PageIndex";
import { SectionScroller } from "./components/SectionScroller";
import { Footer } from "./components/Footer";
import { ThemeToggle } from "./components/ThemeToggle";
import { Panel } from "./panels/Panel";
import { BlogView } from "./blog/BlogView";
import { posts } from "./blog/posts";

type Route =
  | { kind: "home" }
  | { kind: "blog"; postId?: string };

function parseRoute(pathname: string): Route {
  const trimmed = pathname.replace(/\/+$/, "");
  if (trimmed === "/blog" || trimmed === "/blog/") return { kind: "blog" };
  const m = trimmed.match(/^\/blog\/([^/]+)$/);
  if (m) return { kind: "blog", postId: m[1] };
  return { kind: "home" };
}

function routeToPath(route: Route): string {
  if (route.kind === "home") return "/";
  if (route.postId) return `/blog/${route.postId}`;
  return "/blog";
}

export function App() {
  const [active, setActive] = useState(0);
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    function onPop() {
      setRoute(parseRoute(window.location.pathname));
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  function navigate(next: Route) {
    const path = routeToPath(next);
    if (path !== window.location.pathname) {
      window.history.pushState({}, "", path);
    }
    setRoute(next);
  }

  const inBlog = route.kind === "blog";
  const activePostId = inBlog ? route.postId ?? posts[0]?.id : undefined;
  const section = sections[active];

  return (
    <div className="home-app">
      <Frame bg={section.bg}>
        <AnimatePresence mode="wait">
          <motion.section
            key={inBlog ? "blog" : section.id}
            className="home-section"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {inBlog ? (
              <BlogView
                activePostId={activePostId}
                onSelectPost={(id) => navigate({ kind: "blog", postId: id })}
                onBack={() => navigate({ kind: "home" })}
              />
            ) : (
              <Panel
                section={section}
                onEnterBlog={() => navigate({ kind: "blog" })}
              />
            )}
          </motion.section>
        </AnimatePresence>

        {!inBlog && (
          <PageIndex
            sections={sections}
            active={active}
            onSelect={setActive}
          />
        )}
      </Frame>

      <SectionScroller
        count={sections.length}
        active={active}
        setActive={setActive}
        disabled={inBlog}
      />

      <Footer />
      <ThemeToggle />
    </div>
  );
}
